"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { SHELL_INSET_PX } from "@/components/learn-piece/shell/panel-metrics";
import { SubjectChoice } from "@/components/live/camera-subject-choice";
import { VideoWidget } from "@/components/roll-widgets/video-widget";
import { useReplay, type ReplayVideo } from "@/contexts/replay-context";
import { useStudio } from "@/contexts/studio-context";
import { useHandheld, useHandheldPortrait } from "@/hooks/use-short-viewport";
import { segmentAt } from "@/lib/live/replay/replay-cameras";
import { placeCameras } from "@/lib/live/replay/replay-surfaces";
import { wallClockAt, type ReplayMark } from "@/lib/live/replay/replay-song";
import { DEFAULT_SUBJECT } from "@/lib/live/video/camera-framing";
import type { VideoSubject } from "@/lib/live/video/video-types";
import type { Studio } from "@/lib/studio/studio";

/**
 * Les caméras d'un récital enregistré, en vignettes sur le rouleau.
 *
 * # Un fichier, pas un flux
 *
 * En direct, une caméra arrive par la salle et le navigateur montre ce qui
 * tombe. Ici, c'est un fichier déjà écrit, navigable, dont le temps zéro est
 * l'instant de séance de sa première image (`originMs`) : l'élément se place là
 * où en est la tête de lecture, et marche ou s'arrête avec elle.
 *
 * # Une vignette par caméra, plusieurs fichiers par vignette
 *
 * Une caméra laisse un fichier par définition — la montée en couches à
 * l'arrivée, un quart de tour, un zoom qui rogne — et un par republication
 * (`replay-cameras.ts`). Sa vignette reste la même d'un fichier à l'autre :
 * c'est son `src` qui change, à la frontière, et le calage repart du fichier
 * suivant. Le 2026-09-21, chaque fichier faisait sa vignette : les deux
 * premières d'un récital étaient deux morceaux de la même caméra.
 *
 * # Le pont entre les deux temps
 *
 * Le morceau compte en ticks, la vidéo en secondes d'horloge de salle, et les
 * deux n'avancent pas ensemble : mettre le récital en pause fige les ticks et
 * laisse les images défiler. Les marques d'horloge du replay disent à quelle
 * heure chaque tick a sonné (`wallClockAt`), et la grille du morceau — celle
 * que le studio a installée en le chargeant — comble l'intervalle. Les images
 * filmées pendant une pause n'ont pas de tick : on ne les montre jamais, et
 * c'est juste, il ne s'y jouait rien.
 *
 * # Le calage passe par l'élément, pas par React
 *
 * `observeFrames` donne l'élément à son montage ; on suit alors le rouleau
 * image par image. Corriger à chaque rendu React ferait sauter l'image
 * cinquante fois par seconde.
 *
 * # Deux régimes, deux exigences
 *
 * **En lecture**, on ne replace l'élément qu'au-delà de quatre dixièmes de
 * seconde — replacer un `<video>` le fait repartir de son image clé, et l'œil
 * le voit. En deçà, l'écart se rattrape **à la vitesse**, de six pour cent :
 * invisible, et l'image cesse de traîner derrière les notes.
 *
 * **À l'arrêt**, on fait défiler le rouleau à la main pour regarder un
 * passage. L'image ne bouge alors **pas** : elle reste sur sa dernière vue, et
 * la vignette la floute pour dire qu'elle n'est plus à jour. Une seule
 * recherche a lieu, quand la tête s'immobilise, et c'est elle qui rend la
 * netteté.
 *
 * # Une recherche à la fois
 *
 * Le rouleau rend soixante images par seconde ; demander autant de recherches
 * n'en donne pas soixante. Une recherche qu'une autre dépasse est **abandonnée
 * avant d'avoir peint** : l'élément n'affiche alors que la dernière d'une
 * rafale, et l'image avance par à-coups d'un demi-seconde au lieu de suivre.
 *
 * On n'en lance donc qu'une à la fois, et la suivante part de `seeked`, à la
 * position qu'a le rouleau **à ce moment-là** — pas à celle qu'il avait quand
 * on l'a demandée. Chaque recherche peint, et elles s'enchaînent au rythme du
 * décodeur.
 *
 * # Le fichier reste un flux
 *
 * Il y eut une copie en mémoire du fichier entier, faite en tâche de fond pour
 * que le défilement soit vif. Elle répondait à une question qui ne se pose
 * plus : le rouleau cherchait alors une image à chaque cran, et une recherche
 * coûte **137 ms** en flux contre **31 ms** en mémoire (mesuré le 2026-09-18,
 * dans le studio, rouleau tournant) — c'est là qu'un défilement devenait image
 * par image.
 *
 * Depuis, l'image ne bouge plus pendant le geste : elle se fige, se floute, et
 * une **seule** recherche a lieu, quand la tête s'immobilise. Un dixième de
 * seconde une fois par geste, sous le flou qui l'annonce : la copie
 * n'achetait plus rien.
 *
 * Elle coûtait, en revanche. Elle tirait le fichier entier d'un trait — jusqu'à
 * soixante-quatre mégaoctets, et pour chaque caméra — quand l'élément vidéo, lui,
 * ne demande que l'index et la tranche de l'instant regardé. Sur un téléphone,
 * c'est la copie qui gagnait la liaison, et la première image attendait
 * derrière des méga-octets dont personne n'avait besoin tout de suite.
 */

/**
 * En combien de temps le rattrapage referme l'écart, en secondes.
 *
 * Deux secondes : assez lent pour que le changement de vitesse ne se voie pas
 * sur des mains qui jouent, assez vif pour que l'image ne traîne jamais
 * longtemps derrière les notes.
 */
const RECOVERY_SECONDS = 2;

/**
 * De combien on s'autorise à accélérer ou ralentir, au plus.
 *
 * Quinze pour cent. Au-delà, une main se met à jouer au ralenti et cela se
 * voit ; en deçà de dix, un écart d'une demi-seconde mettrait cinq secondes à
 * se refermer et l'image passerait son temps à traîner.
 */
const MAX_NUDGE = 0.15;

/**
 * En deçà, on ne repose pas la vitesse : voir l'écriture dans `follow`.
 *
 * C'est **le seul** garde-fou contre le bruit, et c'est le bon : il se mesure
 * sur ce qui coûte — une écriture de `playbackRate`, donc un retimage du
 * décodeur —, et non sur l'écart, qui ne coûte rien à lire.
 *
 * Il y eut en plus une zone morte sur l'écart : sous quarante millisecondes, la
 * vitesse était remise à 1 tout rond. Elle créait le défaut qu'elle prétendait
 * éviter. La correction étant proportionnelle à l'écart, elle s'éteint à mesure
 * qu'elle referme — et s'éteignait donc **pile au bord de la zone morte**, qui
 * devenait un point fixe : à 0,0401 la vitesse tombait à 0,98, à 0,0399 elle
 * remontait à 1, et l'écart y battait indéfiniment. Mesuré le 2026-09-20 sur un
 * replay : deux écritures à quatre millisecondes d'intervalle, toutes les deux
 * secondes, et une image fantôme à chaque fois. Le défaut n'apparaissait
 * qu'après un premier défilement, parce qu'il faut une recherche pour déposer
 * l'écart sur ce bord-là.
 *
 * Sans zone morte, la correction est continue partout : elle tend vers 1 quand
 * l'écart tend vers zéro, et ce seuil-ci suffit à espacer les écritures.
 */
const RATE_STEP = 0.005;

/**
 * L'écart à partir duquel on saute, en secondes.
 *
 * **Une seconde, et non quatre dixièmes.** À quatre dixièmes, l'image sautait
 * sans arrêt : le rattrapage à six pour cent ne refermait pas l'écart avant
 * qu'il ne repasse le seuil, et chaque saut se voyait — « la vidéo n'arrête
 * pas de sauter », rapporté le 2026-09-19, sur une vignette devenue assez
 * grande pour qu'on y suive les doigts.
 *
 * Un saut n'est donc plus une correction courante mais un aveu d'échec : on ne
 * s'en sert que quand l'image est franchement ailleurs — après une recherche
 * dans le rouleau, un changement de prise, une reprise. Le reste se rattrape à
 * la vitesse, qui ne se voit pas.
 */
const DRIFT_SEEK = 1;

/**
 * La durée d'une image, en secondes : en dessous, chercher ne montrerait rien
 * de neuf. Les caméras de récital filment à trente images par seconde ; en
 * viser trente-cinq laisse la marge pour une caméra un peu plus rapide.
 */
const FRAME_SECONDS = 1 / 35;

/**
 * Le temps d'immobilité de la tête de lecture au bout duquel on considère le
 * geste fini, en millisecondes.
 *
 * Assez court pour que l'image réponde au lâcher, assez long pour ne pas
 * prendre une hésitation au milieu d'un glissé pour une fin.
 */
const SCRUB_QUIET_MS = 140;

/**
 * WebKit — Safari, et tout navigateur d'iPhone — arrête une vidéo par
 * `pause()` ; les autres, par sa vitesse. Voir `halt` dans `follow`.
 */
function haltsByPause(): boolean {
  return typeof navigator !== "undefined" && navigator.vendor === "Apple Computer, Inc.";
}

/** Les événements que WebKit tient pour des gestes, capables de lancer une vidéo. */
const GESTURES = ["touchend", "click", "keydown"] as const;

export function ReplayCamera() {
  const { status, videos, marks, subjects, mine, setCameraSubject, renewLinks, reportVideoReady } = useReplay();
  const { studio } = useStudio();
  const looked = useShowing(videos, marks, studio);
  // Rien de regardé encore pour ces marques : aucune caméra à monter.
  const showing = looked?.marks === marks ? looked.showing : NONE;

  // Aucune caméra ne filme à la position de la tête : la lecture automatique
  // n'a pas d'image à attendre. Seulement une fois le replay prêt — avant, les
  // marques sont vides et ne montrent jamais rien.
  const nothingToShow = status === "ready" && looked?.marks === marks && showing.size === 0;

  useEffect(() => {
    if (nothingToShow) {
      reportVideoReady();
    }
  }, [nothingToShow, reportVideoReady]);
  // Suivi à la rotation, et non retenu au montage : un téléphone arrive
  // toujours par le portrait, et la valeur du montage décrirait l'orientation
  // qu'on vient de quitter. Voir `placeCamera`, où le choix est motivé.
  const handheld = useHandheld();
  const standing = useHandheldPortrait();
  const screen = useMemo(() => ({ handheld, standing }), [handheld, standing]);
  const reserveFoot = useFootReservation(studio);

  // Un rappel par caméra, stable : l'élément se remonterait à chaque rendu si
  // la fonction changeait d'identité, et le décodeur repartirait de zéro.
  const followers = useFollowers(videos, marks, studio, renewLinks, reportVideoReady);
  // Placées ensemble et non chacune pour soi : le pied du rouleau n'a qu'une
  // place, et c'est la liste qui le sait. Voir `placeCameras`. Placées sur
  // **toutes** les caméras et non sur celles qu'on montre : une caméra qui
  // arrive en cours de récital ne doit pas déplacer celle qui filmait déjà.
  const placements = placeCameras(
    videos.map((video) => subjects.get(video.id) ?? DEFAULT_SUBJECT),
    screen,
  );

  if (videos.length === 0) {
    return null;
  }

  return (
    <>
      {videos.map((video, index) => {
        const placement = placements[index];
        const segment = video.segments[showing.get(video.id) ?? -1];

        if (segment === undefined) {
          return null;
        }

        return (
          <VideoWidget
            key={video.id}
            name={video.name}
            label={videos.length > 1 ? `caméra ${index + 1}` : undefined}
            src={segment.url}
            anchorByDefault={placement.anchor}
            collapsedByDefault={placement.collapsed}
            // Un enregistrement n'est pas un direct, et le dire serait faux.
            liveBadge={false}
            observeFrames={followers.get(video.id)}
            onFootprint={placement.foot ? reserveFoot(video.id) : undefined}
            // L'interprète dit ce que filmait la caméra, sur l'image mise en
            // avant — là où il la voit assez grande pour le savoir, comme il
            // la cadre en direct. Qui regarde voit où elle se pose, sans rien
            // à commander.
            controls={
              mine
                ? () => (
                    <SubjectBar
                      subject={subjects.get(video.id) ?? DEFAULT_SUBJECT}
                      onChoose={(subject) => void setCameraSubject(video.id, subject)}
                    />
                  )
                : undefined
            }
          />
        );
      })}
    </>
  );
}

/**
 * Ce que filmait la caméra, au pied de son image mise en avant.
 *
 * C'est ce qui la place : des mains au pied du rouleau, sous la tête de
 * lecture, un visage en bulle dans le coin. La séance l'a dit pendant le
 * récital, et elle a pu se tromper — une webcam restée sur « mains » qui
 * filmait le visage. Le choix change la place de l'image pour tous ceux qui
 * regardent ce replay, et la vignette y part quand on la renvoie dans son coin.
 *
 * La même barre que celle du cadrage en direct, au même endroit, avec le même
 * choix : c'est la même donnée, dite devant la même image.
 */
function SubjectBar({
  subject,
  onChoose,
}: {
  readonly subject: VideoSubject;
  readonly onChoose: (subject: VideoSubject) => void;
}) {
  return (
    <div className="absolute inset-0 flex flex-col justify-end">
      <div
        className="flex items-center justify-between gap-3 px-3 py-2.5"
        style={{
          background: "rgba(11, 11, 16, 0.72)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <span className="text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
          Cette caméra filme
        </span>

        <SubjectChoice subject={subject} onChoose={onChoose} />
      </div>
    </div>
  );
}

/**
 * De quoi dire au rouleau la place que prennent les caméras du pied, pour
 * qu'il remonte sa tête de lecture au-dessus d'elles.
 *
 * Une par caméra, et la plus haute l'emporte : au bureau, deux caméras des
 * mains se partagent le pied, une dans chaque coin (`placeCameras`), et la
 * dernière à mesurer écrasait la réservation de l'autre.
 *
 * On ajoute l'air autour : la vignette est posée par le calque à
 * {@link SHELL_INSET_PX} du clavier, et il en faut autant au-dessus pour que
 * la ligne d'attaque ne lui soit pas collée. Zéro reste zéro — c'est ce que la
 * vignette envoie en partant, et la tête de lecture reprend sa place nominale.
 *
 * Rappels rendus stables, un par caméra : c'est une dépendance d'effet chez la
 * vignette. Et réservation rendue en partant, quoi qu'il arrive — quitter un
 * replay démonte ce composant, pas le studio, qui garderait sinon une
 * réservation pour une image qui n'est plus là.
 */
function useFootReservation(studio: Studio | null): (id: string) => (height: number) => void {
  const [heights] = useState(() => new Map<string, number>());
  const [reporters] = useState(() => new Map<string, (height: number) => void>());

  // Les tables repartent avec le studio : une hauteur mesurée pour l'un ne
  // compte pas pour l'autre, et un rappel donné pour l'un ne parle pas à
  // l'autre.
  useEffect(() => {
    return () => {
      heights.clear();
      reporters.clear();
      studio?.setFootReserve(0);
    };
  }, [studio, heights, reporters]);

  return useCallback(
    (id: string) => {
      let report = reporters.get(id);

      if (report === undefined) {
        report = (height: number) => {
          heights.set(id, height);

          const tallest = Math.max(0, ...heights.values());

          studio?.setFootReserve(tallest === 0 ? 0 : tallest + SHELL_INSET_PX * 2);
        };

        reporters.set(id, report);
      }

      return report;
    },
    [studio, heights, reporters],
  );
}

/**
 * Ce que chaque caméra montre là où en est la tête de lecture : le rang du
 * fichier qui couvre l'instant, par caméra. Une caméra absente de la table n'a
 * rien à montrer.
 *
 * Une caméra branchée en cours de récital n'existe pas avant sa première image.
 * Sa vignette montrait pourtant son image zéro, figée, depuis le début du
 * replay — et `follow` lui reposait `currentTime = 0` à chaque image du
 * rouleau, soit une recherche derrière l'autre, sur le décodeur que l'autre
 * caméra utilise aussi. Elle n'apparaît donc qu'à son heure, et s'en va après
 * sa dernière image. Entre deux de ses fichiers, elle reste (`segmentAt`).
 *
 * Le calcul tourne à chaque image du rouleau, mais l'état ne change qu'aux
 * frontières — quelques fois par récital : c'est la table qu'on compare, pas
 * la position. React ne rend rien quand elle ne bouge pas.
 *
 * Pas de lecture au montage : `onFrame` donnera la réponse à l'image suivante,
 * et une vignette qui paraît une seizième de seconde plus tard ne se voit pas.
 */
const NONE: ReadonlyMap<string, number> = new Map();

/**
 * Le fichier de chaque caméra à montrer, et les marques d'après lesquelles on
 * l'a lu : `null` tant que le rouleau n'a pas encore rendu d'image.
 */
function useShowing(
  videos: readonly ReplayVideo[],
  marks: readonly ReplayMark[],
  studio: Studio | null,
): { marks: readonly ReplayMark[]; showing: ReadonlyMap<string, number> } | null {
  const [looked, setLooked] = useState<{
    marks: readonly ReplayMark[];
    showing: ReadonlyMap<string, number>;
  } | null>(null);

  useEffect(() => {
    if (studio === null) {
      return;
    }

    const transport = studio.getLocalTransport();

    const look = () => {
      const tick = transport.currentTick();
      const timeline = studio.getTransport().timeline;
      const sessionMs = wallClockAt(tick, marks, (at) => timeline.tickToSeconds(at));
      const next = new Map<string, number>();

      // Hors de la prise qu'on regarde, les marques ne valent plus rien : on ne
      // montre aucune caméra plutôt qu'une image prise ailleurs.
      if (Number.isFinite(sessionMs) && tick >= 0 && tick <= lengthOf(studio)) {
        for (const video of videos) {
          const segment = segmentAt(video.segments, sessionMs, video.startMs);

          if (segment !== null) {
            next.set(video.id, segment);
          }
        }
      }

      setLooked((before) =>
        before !== null && before.marks === marks && sameShowing(before.showing, next)
          ? before
          : { marks, showing: next },
      );
    };

    return studio.onFrame(look);
  }, [videos, marks, studio]);

  return looked;
}

function sameShowing(before: ReadonlyMap<string, number>, next: ReadonlyMap<string, number>): boolean {
  if (before.size !== next.size) {
    return false;
  }

  for (const [id, segment] of next) {
    if (before.get(id) !== segment) {
      return false;
    }
  }

  return true;
}

/**
 * Un rappel de calage par caméra, stable : l'élément se remonterait à chaque
 * rendu si la fonction changeait d'identité, et le décodeur repartirait de
 * zéro.
 */
function useFollowers(
  videos: readonly ReplayVideo[],
  marks: readonly ReplayMark[],
  studio: Studio | null,
  renewLinks: () => void,
  onPlayable: () => void,
): Map<string, (element: HTMLVideoElement) => () => void> {
  return useMemo(
    () =>
      new Map(
        videos.map((video) => [
          video.id,
          (element: HTMLVideoElement) => follow(element, video, marks, studio, renewLinks, onPlayable),
        ]),
      ),
    [videos, marks, studio, renewLinks, onPlayable],
  );
}

/**
 * Ce que dure la prise en scène, en ticks.
 *
 * Sans séquence — un morceau seul —, il n'y a pas de bout au-delà duquel se
 * taire : la tête peut courir dans le vide, et l'image reste sur sa dernière
 * position, ce qu'elle faisait déjà.
 */
function lengthOf(studio: Studio): number {
  const sequence = studio.getSequence();
  const entry = sequence.getEntries()[sequence.currentIndex];

  return entry === undefined ? Number.POSITIVE_INFINITY : entry.durationTicks;
}

/**
 * Une position à une image de celle-ci, et dans le fichier.
 *
 * Une image plus loin, ou une image plus tôt quand on est déjà au bout : ce
 * qui compte est que la position **change**, pour que la recherche ait lieu.
 * Voir `primedFor` dans {@link follow}.
 */
function besideIt(at: number, last: number): number {
  return at + FRAME_SECONDS <= last ? at + FRAME_SECONDS : Math.max(0, at - FRAME_SECONDS);
}

/**
 * Cale cet élément sur la tête de lecture du studio, et rend de quoi cesser.
 *
 * Une erreur de l'élément peut venir d'un droit échu — le jeton de son
 * adresse, ou l'adresse présignée du seau vers laquelle l'API l'a redirigé :
 * on le dit au replay (`renewLinks`), qui ne renouvelle que si c'est l'heure.
 *
 * `onPlayable` : l'élément a son image à la position de la tête, de quoi
 * lancer la lecture automatique du replay.
 */
function follow(
  element: HTMLVideoElement,
  video: ReplayVideo,
  marks: readonly ReplayMark[],
  studio: Studio | null,
  renewLinks: () => void,
  onPlayable: () => void,
): () => void {
  if (studio === null) {
    return () => {};
  }

  const transport = studio.getLocalTransport();

  /**
   * La dernière recherche demandée.
   *
   * Sans elle, une position que l'élément ne peut pas atteindre — au-delà de la
   * dernière image, ou avant que la durée soit connue — se redemanderait à
   * chaque `seeked`, indéfiniment.
   */
  let asked: number | null = null;
  /**
   * L'élément a-t-il reçu une recherche d'amorce.
   *
   * iOS ne précharge pas sur réseau mobile : `preload="auto"` y est un vœu, et
   * tant que personne ne lui demande un instant précis, le fichier reste
   * fermé après ses métadonnées. Or on ne demande rien quand la tête de
   * lecture tombe **déjà** au bon endroit — l'écart est sous la durée d'une
   * image —, et c'est exactement le cas d'une caméra qui a commencé à filmer
   * avec le récital : le temps du fichier et celui du rouleau partent
   * ensemble.
   *
   * Sur un replay à deux caméras, ça se voyait : celle branchée avant le
   * début se chargeait, l'autre restait en chargement sans fin. Vu sur iPhone
   * en 5G le 2026-09-21.
   *
   * On force donc **une** recherche au premier passage où la durée est connue,
   * décalée d'une image quand la position visée est déjà celle de l'élément :
   * un `currentTime` reposé à l'identique n'est pas garanti de chercher, donc
   * pas garanti de charger. Une par **fichier** : la vignette en change en
   * chemin, et chacun doit s'ouvrir. D'où l'adresse retenue, et non un
   * drapeau.
   *
   * Sous iOS, c'est même la seule porte : le fichier y est posé en
   * `preload="metadata"`, et c'est cette recherche qui l'ouvre (voir
   * `filePreload`, `video-widget.tsx`). Une vignette qui ne la reçoit pas
   * reste en chargement pour toujours.
   */
  let primedFor: string | null = null;
  /** Les adresses des fichiers telles que l'élément les rend : absolues. */
  const absolute = new Map<string, string>();

  const resolved = (url: string): string => {
    let href = absolute.get(url);

    if (href === undefined) {
      href = new URL(url, document.baseURI).href;
      absolute.set(url, href);
    }

    return href;
  };

  /** Où en était la tête à l'image précédente, et depuis quand elle n'a pas bougé. */
  let lastTarget = Number.NaN;
  let stillSince = 0;
  /** Le rappel qui constatera la fin du geste si le rouleau ne rend plus d'image. */
  let settle: ReturnType<typeof setTimeout> | undefined;

  /*
    Arrêter l'image sans arrêter l'élément.

    Samsung Internet refuse tout `play()` qui ne part pas directement d'un
    geste : la lecture acceptée au tap sur « Regarder » n'y débloque rien pour
    la suite, pas plus que l'activation encore valable deux secondes plus tard.
    Or le calage arrêtait la vidéo au tap, le temps du décompte, puis la
    relançait par `play()` : refusé à chaque image, et la vidéo n'avançait plus
    que par les sauts du calage, image par image. Mesuré sur l'appareil, par le
    débogage USB, le 2026-09-29 — c'est le navigateur qu'ouvrent les liens de
    WhatsApp et de Discord sur un Samsung.

    Une vidéo lancée n'est donc plus arrêtée : elle est tenue à vitesse nulle,
    et le rattrapage lui rend sa vitesse quand le rouleau repart, sans rappeler
    `play()`. Sauf sous WebKit, où un élément lancé pendant un geste reste
    débloqué pour de bon et où l'arrêt par `pause()` est éprouvé — la vitesse
    nulle, elle, ne l'y est pas.
  */
  const byPause = haltsByPause();

  const halt = () => {
    if (byPause) {
      element.pause();

      return;
    }

    if (!element.paused && element.playbackRate !== 0) {
      element.playbackRate = 0;
    }
  };

  const apply = (tick: number) => {
    /*
      La place de l'image suit la caméra, et se repose à chaque image du
      rouleau.

      Elle est lue ici, et non posée par React : la courbe dit où le clavier
      filmé se trouve **à cet instant du fichier**, et cet instant change
      soixante fois par seconde. Passer par un rendu en ferait autant de
      rendus pour une transformation CSS.
    */
    // Une recherche est en cours : la doubler la ferait abandonner avant
    // qu'elle ait peint. `seeked` reprendra, d'où en sera le rouleau alors.
    if (element.seeking) {
      return;
    }

    // La tête peut sortir du morceau courant : le rouleau descend sous zéro
    // dans la prise d'avant, et court après la dernière note. Les marques, elles,
    // ne valent que pour la prise qu'on regarde — les prolonger montrerait le
    // film à un endroit qui n'a rien à voir.
    if (tick < 0 || tick > lengthOf(studio)) {
      halt();

      return;
    }

    // Du tick à l'heure de la salle, puis au fichier de cet instant, puis au
    // temps de ce fichier.
    const timeline = studio.getTransport().timeline;
    const sessionMs = wallClockAt(tick, marks, (at) => timeline.tickToSeconds(at));
    const index = Number.isFinite(sessionMs) ? segmentAt(video.segments, sessionMs, video.startMs) : null;
    const segment = index === null ? undefined : video.segments[index];

    // La caméra ne filmait pas : avant sa première image, après sa dernière,
    // ou rideau baissé. Sa vignette n'est plus montée à ce moment-là
    // (`useShowing`) ; le temps que React la démonte, on ne redemande rien.
    if (segment === undefined) {
      halt();
      asked = null;

      return;
    }

    // Le fichier de cet instant n'est pas encore celui de l'élément : React le
    // pose au rendu qui suit la frontière. D'ici là, on ne cherche rien dans
    // l'autre, dont le temps ne correspond plus à rien.
    if (element.src !== resolved(segment.url)) {
      return;
    }

    const target = (sessionMs - segment.originMs) / 1000;

    // Dans une couture, juste avant la première image du fichier : on montre
    // celle-là, à l'arrêt, plutôt que de la redemander à chaque image du
    // rouleau — ce serait une recherche derrière l'autre.
    if (target < 0) {
      halt();

      if (element.currentTime > FRAME_SECONDS) {
        element.currentTime = 0;
      }

      asked = null;

      return;
    }

    // Le rouleau court, l'image court ; le rouleau s'arrête, l'image s'arrête.
    const running = !transport.isDetached;

    if (!running) {
      halt();
    }

    // Après la dernière image, l'élément reste dessus : la demander au-delà de
    // la fin ne la changerait pas, et le rouleau va plus loin que la caméra.
    const last = Number.isFinite(element.duration)
      ? Math.max(0, element.duration - FRAME_SECONDS)
      : target;
    const wanted = Math.min(target, last);
    /*
      Pendant le geste, l'image ne bouge pas.

      On a d'abord cherché l'image exacte à chaque cran, puis une image
      approchée. Les deux donnaient le même défaut : une image qui saute d'un
      endroit à l'autre du film pendant qu'on fait glisser le rouleau, pour
      montrer, sur une caméra posée au-dessus d'un clavier, des vues presque
      identiques. Elle reste donc **figée** tant que la tête bouge, et la
      vignette la floute (`video-widget.tsx`) : ce qu'on voit n'est plus à
      jour, et le dit.

      Une seule recherche a lieu, quand la tête s'immobilise — et c'est elle
      qui rend la netteté. Le décodeur ne travaille plus qu'une fois par geste
      au lieu de dix, ce qui vaut double avec deux caméras.
    */
    const now = performance.now();

    // La première position vue n'est pas un geste : rien ne bougeait avant.
    if (wanted !== lastTarget) {
      stillSince = Number.isNaN(lastTarget) ? now - SCRUB_QUIET_MS : now;
      lastTarget = wanted;
    }

    const moving = !running && now - stillSince < SCRUB_QUIET_MS;

    // Écrit seulement quand il change : `apply` tourne à chaque image du
    // rouleau, et reposer soixante fois par seconde un attribut qui ne bouge
    // pas fait recalculer le style de l'élément pour rien — sur une vidéo, ce
    // style porte un filtre, donc une couche à recomposer.
    if (element.dataset.scrubbing !== String(moving)) {
      element.dataset.scrubbing = String(moving);
    }

    /*
      La fin du geste se constate à une image qui n'a plus bougé — encore
      faut-il qu'il y en ait une. Rouleau immobile, le ticker se parke
      (`studio-view.ts`) et `onFrame` se tait : la recherche de fin, et avec
      elle celle d'amorce, n'avait plus que les événements de l'élément pour
      passer, et des métadonnées arrivées pendant les premières millisecondes
      laissaient un fichier jamais amorcé — donc jamais chargé là où le
      navigateur ne précharge pas. On se rappelle donc soi-même, une fois le
      calme revenu.
    */
    if (moving) {
      clearTimeout(settle);
      settle = setTimeout(chain, SCRUB_QUIET_MS - (now - stillSince));

      return;
    }

    const drift = element.currentTime - wanted;
    const reached = Math.abs(drift) <= (running ? DRIFT_SEEK : FRAME_SECONDS);
    const again = asked !== null && Math.abs(asked - wanted) <= FRAME_SECONDS;

    if (primedFor !== element.src && Number.isFinite(element.duration)) {
      primedFor = element.src;
      asked = wanted;
      element.currentTime = reached ? besideIt(wanted, last) : wanted;
    } else if (!reached && !again) {
      asked = wanted;
      element.currentTime = wanted;
    }

    /*
      Le rattrapage fin se fait à la vitesse, pas au saut.

      Une tolérance large laissait l'image traîner jusqu'à un tiers de seconde
      derrière les notes sans que rien ne la corrige — « la vidéo est un peu en
      retard », rapporté le 2026-09-19. La resserrer en sautant aurait donné
      une image qui saccade : un `<video>` replacé repart de son image clé. On
      pousse donc de six pour cent jusqu'à ce que l'écart se referme, ce qui ne
      se voit pas.

      La latence de sortie audio n'entre pas ici : le transport la retranche
      déjà pour viser l'oreille (`audio-timebase.ts`), donc la tête de lecture,
      le son et l'image parlent du même instant.
    */
    if (running) {
      /*
        Le rattrapage est **proportionnel** à l'écart, et non d'un pas fixe.

        Un pas fixe de six pour cent mettait huit secondes à refermer une
        demi-seconde : l'écart repassait le seuil de saut avant d'être résorbé,
        et l'image sautait en boucle. Proportionnel, un grand écart se corrige
        vite et un petit doucement, ce qui est exactement ce qu'on veut : la
        correction se fait sentir quand il le faut et s'éteint d'elle-même.

        Et **sans seuil sous lequel on remettrait 1 tout rond** : ce seuil-là
        était un point fixe, et l'image y battait. Voir `RATE_STEP`.
      */
      const correction = Math.max(-MAX_NUDGE, Math.min(MAX_NUDGE, drift / RECOVERY_SECONDS));
      const rate = 1 - correction;

      // Écrite seulement quand elle change vraiment : `apply` tourne à chaque
      // image du rouleau, et reposer soixante fois par seconde une vitesse qui
      // ne bouge que d'un millième fait retimer le décodeur pour rien.
      if (Math.abs(element.playbackRate - rate) > RATE_STEP) {
        element.playbackRate = rate;
      }
    }

    if (running && element.paused) {
      void element.play().catch((cause: unknown) => {
        // Le navigateur refuse de lancer une vidéo : elle reste sur son image,
        // et le geste suivant la lancera (`permit`).
        if (cause instanceof Error && cause.name === "NotAllowedError") {
          askGesture();
        }
      });
    }
  };

  // La recherche suivante part d'ici, et non du rouleau : c'est ce qui les
  // enchaîne au rythme du décodeur plutôt qu'à celui de l'affichage.
  const chain = () => {
    apply(transport.currentTick());
  };

  // La durée n'était pas connue : ce qu'on n'avait pas pu atteindre est à
  // retenter, maintenant qu'on sait jusqu'où va le fichier.
  const ready = () => {
    asked = null;
    chain();
  };

  // Prête quand la recherche d'amorce a abouti et que la suite est là : avant
  // elle, l'image montrée est celle du début du fichier, pas celle de la tête.
  const playable = () => {
    if (primedFor === element.src && !element.seeking && element.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      onPlayable();
    }
  };

  /*
    La lecture, permise sous un geste.

    Le `play()` du calage part de la boucle d'images, jamais d'un geste : c'est
    le rouleau qui décide quand la vidéo marche. Sous iOS en économie
    d'énergie, WebKit refuse alors de lancer une vidéo, même muette. Elle
    restait à l'arrêt, et le calage la faisait sauter vers la tête chaque fois
    qu'elle avait une seconde de retard : une image par seconde, « la vidéo
    avance très saccadée », rapporté le 2026-09-29.

    Chrome sous Android fait de même quand la lecture automatique est coupée
    dans ses réglages de sites : même symptôme, même remède.

    Le navigateur lève cette bride, pour l'élément et pour de bon, quand on le
    lance pendant un geste. Le premier geste venu — n'importe où, et d'abord le
    clic sur « Regarder », puisque les caméras se montent derrière la page de
    la prise — lance donc la vidéo, et l'arrête aussitôt si le rouleau ne
    marche pas (`halt` : à vitesse nulle, hors WebKit). Un refus plus tard (une caméra montée en cours de prise) réarme
    l'écoute : le geste suivant la relance.

    L'écoute ne se retire qu'une fois la lecture **acceptée** — lancée, ou
    seulement interrompue par la pause qui suit. Tout `touchend` n'est pas un
    geste : celui qui finit un défilement n'en est pas un. Se retirer au
    premier venu laissait la page d'une prise, qu'on fait défiler avant de
    toucher « Regarder », sans personne pour écouter le vrai clic : la vidéo
    était refusée après le décompte, et ne repartait qu'au tap suivant
    (Android, 2026-09-29).

    `touchend` et `click`, et non `pointerdown` : ce sont eux que les
    navigateurs tiennent pour des gestes.
  */
  const permit = () => {
    const attempt = element.play();

    if (transport.isDetached) {
      halt();
    }

    attempt.then(dropGesture, (cause: unknown) => {
      // Refusée : ce geste n'en était pas un pour le navigateur. Le suivant
      // retentera. Interrompue par notre arrêt : la lecture était acceptée.
      if (!(cause instanceof Error && cause.name === "NotAllowedError")) {
        dropGesture();
      }
    });
  };

  function askGesture(): void {
    for (const type of GESTURES) {
      window.addEventListener(type, permit, true);
    }
  }

  function dropGesture(): void {
    for (const type of GESTURES) {
      window.removeEventListener(type, permit, true);
    }
  }

  askGesture();
  element.addEventListener("seeked", chain);
  element.addEventListener("seeked", playable);
  element.addEventListener("canplay", playable);
  element.addEventListener("loadedmetadata", ready);
  element.addEventListener("error", renewLinks);
  apply(transport.currentTick());

  const stop = studio.onFrame(apply);

  return () => {
    element.removeEventListener("seeked", chain);
    element.removeEventListener("seeked", playable);
    element.removeEventListener("canplay", playable);
    element.removeEventListener("loadedmetadata", ready);
    element.removeEventListener("error", renewLinks);
    dropGesture();
    clearTimeout(settle);
    stop();
  };
}
